import { useState, useEffect } from 'react';
import { ReactComponent as CalendarIcon } from '~/assets/svg/calendar.svg';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { publicClient } from '~/services';
import { Calendar, Schedule } from '~/types';
import { useTranslation } from 'react-i18next';

const CalendarScreen = ({
  scheduleData,
  calendarsData,
}: {
  scheduleData: any | Schedule;
  calendarsData: Calendar[];
}) => {
  const { schedule_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation('calendar');
  
  // Check if this is preview mode and get agentId from query params
  const isPreviewMode = schedule_id === 'preview';
  const urlParams = new URLSearchParams(location.search);
  const agentId = urlParams.get('agentId');
  
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedLocationIndex, setSelectedLocationIndex] = useState<
    number | null
  >(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [isPast, setIsPast] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [index, setIndex] = useState<number | null>(null);

  // Define the type for calendar
  type Calendar = {
    type: string;
    meetingDetails?: {
      headquarters?: string;
      address?: string;
    };
  };

  // Extract locations from calendarsData
  const locations = calendarsData
    ?.filter(
      (calendar: any) =>
        calendar.type === 'PERSONAL' && calendar.meetingDetails?.headquarters,
    )
    ?.map((calendar: any) => ({
      _id: calendar._id,
      type: calendar?.meetingType,
      name: `${calendar?.meetingDetails?.headquarters} - ${calendar?.meetingDetails?.address}`,
    })) || [{}];

  // For preview mode, we also want to show all available calendars as potential locations
  const previewLocations = isPreviewMode ? calendarsData
    ?.filter((calendar: any) => calendar.meetingDetails?.headquarters)
    ?.map((calendar: any) => ({
      _id: calendar._id,
      type: calendar?.meetingType,
      name: `${calendar?.meetingDetails?.headquarters} - ${calendar?.meetingDetails?.address || ''}`,
    })) || [] : [];
  
  // Use preview locations if in preview mode and regular locations otherwise
  const availableLocations = isPreviewMode ? previewLocations : locations;

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    const scheduleStartDate = scheduleData?.start_date?.date;
    const scheduleStatus = scheduleData?.status;

    if (scheduleStartDate && new Date(scheduleStartDate) < new Date()) {
      setIsPast(true);
    }

    // Check if the appointment is rejected/canceled
    if (scheduleStatus === 'REJECTED') {
      setIsRejected(true);
    }

    // Only proceed if scheduleData exists (not in preview mode)
    if (scheduleData && scheduleData.calendar_rulers_id) {
      const calendarLocationIndex = availableLocations.findIndex(
        (calendar: any) => calendar._id === scheduleData.calendar_rulers_id,
      );
      setSelectedLocation(availableLocations[calendarLocationIndex]?.name || '');
      setIndex(calendarLocationIndex);
      const calendarRuler = calendarsData.filter(
        (calendar: any) => calendar._id === scheduleData.calendar_rulers_id,
      )[0] as any;

      if (calendarRuler) {
        const meetingType = calendarRuler.meetingType;
        setSelectedMeetingType(t(meetingType));
      }
    } else {
      // Handle preview mode or when scheduleData is null
      if (calendarsData && calendarsData.length > 0) {
        // Use the first calendar as default for preview - but with real data
        const firstCalendar = calendarsData[0] as any;
        
        // Set default meeting type based on the first calendar's type
        setSelectedMeetingType(t(firstCalendar?.meetingType || 'VIRTUAL'));
        
        // If there are locations available, use the first one
        if (availableLocations && availableLocations.length > 0 && availableLocations[0].name) {
          setSelectedLocation(availableLocations[0].name);
          setIndex(0);
        } else {
          // If no specific location, but it's an in-person meeting, use headquarters if available
          if (firstCalendar?.meetingType === 'IN_PERSON' && firstCalendar?.meetingDetails?.headquarters) {
            setSelectedLocation(`${firstCalendar.meetingDetails.headquarters} - ${firstCalendar.meetingDetails.address || ''}`);
            setIndex(0);
          }
        }
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const meetingModes = [t('virtual'), t('in_person')];

  // Habilitamos el botón si se selecciona "Virtual" o si es "Presencial" y se ha elegido una sede
  // Pero solo si la cita no está en pasado y no está rechazada/cancelada
  const isButtonEnabled =
    !isPast &&
    !isRejected &&
    (selectedMeetingType === t('virtual') ||
      (selectedMeetingType === t('in_person') && selectedLocation));

  if (!calendarsData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="alert alert-brand-error">
          <p className="text-brand-body">{t('schedule_error')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-base-100">
      {/* Panel izquierdo */}
      <div className="w-full p-8 md:w-1/2">
        <div className="card-brand">
          <div className="card-body">
            <h2 className="text-brand-heading mb-4">
              {t('meeting_type')}
            </h2>
            <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-6">
              {meetingModes.map((mode) => (
                <label key={mode} className="flex cursor-pointer items-center">
                  <input
                    type="radio"
                    name="meetingType"
                    value={mode}
                    checked={selectedMeetingType === mode}
                    onChange={(e) => {
                      if (!isPast && !isRejected) {
                        setSelectedMeetingType(e.target.value);
                        // Reinicia la sede si se cambia de tipo
                        if (e.target.value !== t('in_person'))
                          setSelectedLocation('');
                      }
                    }}
                    className="radio radio-primary"
                    disabled={isPast || isRejected}
                  />
                  <span className="ml-3 text-brand-body">{mode}</span>
                </label>
              ))}
            </div>

            {selectedMeetingType === t('in_person') && (
              <div className="mt-6">
                <label className="text-brand-heading block mb-3">
                  {t('select_location')}
                </label>
                <select
                  className="select select-bordered w-full input-brand"
                  value={selectedLocation}
                  onChange={(e) => {
                    if (!isPast && !isRejected) {
                      setSelectedLocation(e.target.value);
                      setSelectedLocationIndex(e.target.selectedIndex - 1); // Subtract 1 to account for the default option
                      
                      // Update the index to match the selected location
                      const selectedLocationIndex = availableLocations.findIndex(
                        (location: any) => location.name === e.target.value
                      );
                      setIndex(selectedLocationIndex >= 0 ? selectedLocationIndex : null);
                    }
                  }}
                  disabled={isPast || isRejected}
                >
                  <option value="">{t('select_location')}</option>
                  {availableLocations.map((location: any, index: any) => (
                    <option key={index} value={location.name}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {(isPast && scheduleData?.start_date?.date) && (
          <div className="mt-6">
            <div className="alert alert-brand-warning">
              <div>
                <h3 className="font-semibold">{t('appointment_passed')}</h3>
                <p className="text-sm mt-1">
                  {t('appointment_date')}:{' '}
                  <span className="font-bold">
                    {new Date(scheduleData.start_date.date).toLocaleDateString()}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {isRejected && scheduleData?.start_date?.date && (
          <div className="mt-6">
            <div className="alert alert-brand-error">
              <div>
                <h3 className="font-semibold">{t('appointment_canceled')}</h3>
                <p className="text-sm mt-1">
                  Tu cita programada para el{' '}
                  <span className="font-bold">
                    {new Date(scheduleData.start_date.date).toLocaleDateString()}
                  </span>
                  {' '}ha sido cancelada.
                  {scheduleData.cancel_reason && (
                    <>
                      <br />
                      <span className="text-xs opacity-80">
                        {t('canceled_reason')}: {scheduleData.cancel_reason}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
            
            {/* Reschedule button for rejected appointments */}
            <div className="mt-4">
              <button
                className="btn btn-primary rounded-full w-full"
                onClick={() => navigate(`/calendar/${schedule_id}/create_new_schedule`)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('reschedule_appointment')}
              </button>
            </div>
          </div>
        )}

        <div className="mt-8">
          <button
            className={`btn w-full ${
              isButtonEnabled
                ? 'btn btn-primary rounded-full'
                : 'btn btn-disabled'
            }`}
            disabled={!isButtonEnabled}
            onClick={() => {
              if (isButtonEnabled) {
                let selectedCalendars: any[] = [];
                if (selectedMeetingType === t('virtual')) {
                  selectedCalendars = calendarsData.filter(
                    (calendar: any) => calendar.meetingType === 'VIRTUAL',
                  );
                } else {
                  // For in-person meetings, find the calendar that matches the selected location
                  if (selectedLocation) {
                    const selectedCalendar = availableLocations.find(
                      (location: any) => location.name === selectedLocation
                    );
                    if (selectedCalendar) {
                      const matchingCalendar = calendarsData.find(
                        (calendar: any) => calendar._id === selectedCalendar._id
                      );
                      selectedCalendars = matchingCalendar ? [matchingCalendar] : [];
                    } else {
                      selectedCalendars = [];
                    }
                  } else {
                    selectedCalendars = [];
                  }
                }
                
                // Construct the navigation URL, preserving agentId for preview mode
                const baseUrl = `/calendar/${schedule_id}/scheduler`;
                const navigationUrl = isPreviewMode && agentId 
                  ? `${baseUrl}?agentId=${agentId}`
                  : baseUrl;
                
                // Debug logging for preview mode
                if (isPreviewMode) {
                  console.log('Preview mode navigation:', {
                    selectedMeetingType,
                    selectedLocation,
                    selectedCalendars,
                    availableLocations,
                    index
                  });
                }
                
                if (index !== null) {
                  navigate(navigationUrl, {
                    state: {
                      selectedCalendars,
                      typeName: selectedCalendars.length > 0 ? selectedCalendars[0].meetingType : null,
                    },
                  });
                } else {
                  navigate(navigationUrl, {
                    state: { 
                      selectedCalendars,
                      typeName: selectedCalendars.length > 0 ? selectedCalendars[0].meetingType : null,
                    },
                  });
                }
              }
            }}
          >
            {t('confirm')}
          </button>
        </div>
      </div>

      {/* Panel derecho */}
      {isDesktop && (
        <div className="hidden md:flex w-1/2 items-center justify-center bg-brand-gradient rounded-r-xl">
          <div className="text-center">
            <CalendarIcon className="w-64 h-64 text-white opacity-80 mx-auto mb-4" />
            <h3 className="text-brand-heading text-white text-2xl">
              {t('schedule_appointment')}
            </h3>
            <p className="text-white/80 mt-2 text-lg">
              {t('select_preferences')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarScreen;
